import { ECountry, COUNTRIES, COUNTRY_PREFIXES } from '@constants';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PhoneInputWrapper, PrefixSelector, PhoneNumberInput } from './PhoneInput.styles';

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  defaultCountry?: ECountry;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  defaultCountry = ECountry.ES
}) => {
  const { t } = useTranslation('DefaultTranslations');
  const [selectedCountry, setSelectedCountry] = useState<ECountry>(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');

  const countryOptions = useMemo(() => {
    return COUNTRIES.map(country => ({
      value: country,
      label: COUNTRY_PREFIXES[country]
    }));
  }, [t]);

  useEffect(() => {
    if (value) {
      const countryEntry = Object.entries(COUNTRY_PREFIXES).find(([_, prefix]) =>
        value.startsWith(prefix)
      );

      if (countryEntry) {
        const [country, prefix] = countryEntry;
        setSelectedCountry(country as ECountry);
        setPhoneNumber(value.substring(prefix.length));
      } else if (value.startsWith('+')) {
        setPhoneNumber(value);
      } else {
        setPhoneNumber(value);
      }
    } else {
      setPhoneNumber('');
    }
  }, [value]);

  const handleCountryChange = (newValue: unknown) => {
    const country = newValue as ECountry;
    setSelectedCountry(country);
    const prefix = COUNTRY_PREFIXES[country];
    onChange?.(prefix + phoneNumber);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/\D/g, '');
    setPhoneNumber(newNumber);
    const prefix = COUNTRY_PREFIXES[selectedCountry];
    onChange?.(prefix + newNumber);
  };

  return (
    <PhoneInputWrapper>
      <PrefixSelector
        value={selectedCountry}
        onChange={handleCountryChange}
        options={countryOptions}
        disabled={disabled}
        showSearch
        optionLabelProp="label"
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: {
              width: 'max-content',
              minWidth: 280
            }
          }
        }}
        filterOption={(input, option) => {
          const searchTerm = input.toLowerCase();
          const countryName = t(option?.value as ECountry).toLowerCase();
          const prefix = COUNTRY_PREFIXES[option?.value as ECountry];

          return countryName.includes(searchTerm) || prefix.includes(searchTerm);
        }}
        optionRender={option => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}>
            <span style={{ flexShrink: 0 }}>{t(option.value as ECountry)}:</span>
            <span style={{ fontWeight: 500, color: '#666', flexShrink: 0, marginLeft: 8 }}>
              {option.label}
            </span>
          </div>
        )}
      />
      <PhoneNumberInput
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    </PhoneInputWrapper>
  );
};
